import { useEffect, useRef, useState, useCallback } from "react";
import { WS_URL } from "../lib/api";

export function useOrdersSocket(onMessage) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(null);
  const pingRef = useRef(null);
  const dropRef = useRef(null);
  const cbRef = useRef(onMessage);
  cbRef.current = onMessage;

  const clearPing = () => {
    if (pingRef.current) {
      clearInterval(pingRef.current);
      pingRef.current = null;
    }
  };

  const connect = useCallback(() => {
    try {
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;
      ws.onopen = () => {
        if (dropRef.current) { clearTimeout(dropRef.current); dropRef.current = null; }
        setConnected(true);
        if (cbRef.current) cbRef.current({ type: "reconnected" });
        // keepalive ping so proxies don't close idle connections (important in production)
        clearPing();
        pingRef.current = setInterval(() => {
          try {
            if (ws.readyState === WebSocket.OPEN) ws.send("ping");
          } catch {}
        }, 20000);
      };
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          // Ignore server heartbeats; they only keep the socket alive.
          if (data && data.type === "heartbeat") return;
          if (cbRef.current) cbRef.current(data);
        } catch {}
      };
      ws.onclose = () => {
        clearPing();
        // Debounce the "disconnected" badge: a quick reconnect shouldn't flash red.
        if (!dropRef.current) {
          dropRef.current = setTimeout(() => { setConnected(false); dropRef.current = null; }, 3000);
        }
        retryRef.current = setTimeout(connect, 2000);
      };
      ws.onerror = () => {
        ws.close();
      };
    } catch {
      retryRef.current = setTimeout(connect, 2000);
    }
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (retryRef.current) clearTimeout(retryRef.current);
      if (dropRef.current) clearTimeout(dropRef.current);
      clearPing();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { connected };
}
